import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall, apiCallEx } from "../../apiClient";
import { Card, Badge, SectionTitle, Btn } from "../primitives";
import { GlobalAiConsole } from "./GlobalAiConsole";
import type { UserRow } from "../../types/api";

// Consolidated AI routing screen (Admin ▸ AI & Models ▸ AI Routing).
//
// This is the ONE place AI routing is managed. Two clearly-separated sections,
// each shown only to whoever is authorized for it:
//
//   1. Platform AI routing (operator) — the model catalog, per-tier routing and the
//      KontractAI platform default keys. Rendered ONLY for is_operator ||
//      is_ai_routing_operator (the client mirror of the backend `operatorOnly` gate:
//      a full walls-up operator, OR the walls-down in-house `admin.manage_ai_routing`
//      holder). Every control inside GlobalAiConsole calls /platform/ai-routing/* or
//      /ai-default-key, which are `operatorOnly`-gated server-side — so this render
//      gate is defense-in-depth, never the sole enforcement. A plain admin who can
//      reach this tab but lacks operator access does NOT see this section and could
//      not use it even if they did (the API 403s them).
//
//   2. Your organization's AI keys (bring-your-own) — the existing per-tenant BYO
//      keys, backed by /settings/ai-key (gated by admin.manage_integrations). Shown
//      to whoever reaches this tab, exactly as before; the endpoints remain the
//      authority on who may actually read/write them.
//
// Consolidating the operator console here retired the separate Sidebar "AI Routing"
// item for walls-down operators — this is now the single, discoverable surface.
const PROVIDERS: { id: string; label: string; placeholder: string }[] = [
  { id: "anthropic", label: "Anthropic", placeholder: "sk-ant-…" },
  { id: "openai", label: "OpenAI", placeholder: "sk-…" },
  { id: "groq", label: "Groq", placeholder: "gsk_…" },
];

const ProviderKeyCard = ({ id, label, placeholder }: { id: string; label: string; placeholder: string }) => {
  const [configured, setConfigured] = useState(false);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const r = await apiCall(`/settings/ai-key?provider=${id}`);
    if (r) setConfigured(!!r.configured);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!input.trim()) return;
    setSaving(true); setErr(""); setMsg("");
    const r = await apiCallEx('/settings/ai-key', { method: 'PUT', body: JSON.stringify({ provider: id, api_key: input }) });
    setSaving(false);
    if (r.ok && r.data?.success) { setInput(""); setMsg(`${label} key validated and saved. Your ${label} usage now runs on your key.`); load(); }
    else { setErr(r.data?.message || (r.data?.error === 'invalid_key' ? "That AI key could not be validated with the provider and was not saved." : "Could not save the key.")); }
  };
  const clear = async () => {
    if (!window.confirm(`Remove your ${label} key? ${label} usage will fall back to the KontractAI default.`)) return;
    setSaving(true); setErr(""); setMsg("");
    const r = await apiCallEx(`/settings/ai-key?provider=${id}`, { method: 'DELETE' });
    setSaving(false);
    if (r.ok) { setMsg(`${label} key removed — running on the KontractAI default.`); load(); } else { setErr("Could not remove the key."); }
  };

  return (
    <Card style={{ marginBottom: 12, border: `1px solid ${configured ? T.grn : T.brd}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.tx }}>{label}</div>
        {configured && <Badge c={T.grn} b={T.grnS}>On your key</Badge>}
      </div>
      {err && <div style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 4, backgroundColor: T.redS, color: T.red, fontSize: 12 }}>{err}</div>}
      {msg && <div style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 4, backgroundColor: T.grnS, color: T.grn, fontSize: 12 }}>{msg}</div>}
      {configured ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: T.txM }}>Your {label} key is set.</span>
          <Btn v="ghost" sz="sm" disabled={saving} onClick={clear}>Remove key</Btn>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input type="password" autoComplete="off" value={input} onChange={e => setInput(e.target.value)} placeholder={placeholder} style={{ flex: 1, minWidth: 240, padding: "7px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 13, fontFamily: "monospace" }} />
          <Btn v="primary" sz="sm" disabled={saving || !input.trim()} onClick={save}>{saving ? "Validating…" : "Save & validate"}</Btn>
        </div>
      )}
    </Card>
  );
};

export const AiRoutingAdmin = ({ currentUser = null }: { currentUser?: UserRow | null }) => {
  // Mirror of the backend `operatorOnly` gate. Full walls-up operator OR the
  // walls-down in-house admin.manage_ai_routing holder. Defense-in-depth only —
  // /platform/* is `operatorOnly`-gated regardless of what renders here.
  const isOperator = !!(currentUser?.is_operator || currentUser?.is_ai_routing_operator);

  return (
    <div>
      {isOperator && (
        <div style={{ marginBottom: 28 }}>
          <SectionTitle sub="Model catalog, per-tier routing and KontractAI platform default keys — operator-controlled, applies to the whole deployment">Platform AI routing</SectionTitle>
          <GlobalAiConsole />
        </div>
      )}

      {isOperator && <div style={{ borderTop: `1px solid ${T.brd}`, margin: "8px 0 24px" }} />}

      <SectionTitle sub="Bring your own AI provider keys">Your organization's AI keys</SectionTitle>

      <Card style={{ marginBottom: 16, backgroundColor: T.bg3, border: `1px dashed ${T.brd}` }}>
        <div style={{ fontSize: 12.5, color: T.txM, lineHeight: 1.5 }}>
          Bring your own AI keys. For each provider, if you enter your own API key, your usage for that
          provider runs on your key{isOperator ? " — overriding the platform default key above for this organization" : ""}.
          If a provider's field is left empty, that provider falls back to the
          KontractAI {isOperator ? "platform default" : "default"} key. Keys are validated when you save,
          encrypted at rest, and never shown again. KontractAI's default keys are never displayed here.
        </div>
      </Card>

      {PROVIDERS.map(p => <ProviderKeyCard key={p.id} {...p} />)}

      {/* The spend view MOVED on 2026-07-30 to its own screen, Admin ▸ AI &
          Models ▸ AI usage and costs. It had grown from a summary into a full
          screen of its own, and reading it meant scrolling past all the routing
          configuration above to reach it. Routing is a settings screen you
          change; spend is a reporting screen you read. They are now separate.
          Same permission gates both. */}
    </div>
  );
};
